/**
 * Custom React Flow node for one agent in a set graph. Shows the member name,
 * its label, the model, and a type badge; entry ("main") agents get a primary
 * ring, external references render faded/dashed.
 */
import { Handle, Position } from 'reactflow';
import Sheet from '@mui/joy/Sheet';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';

export const NODE_WIDTH = 230;
export const NODE_HEIGHT = 104;

export default function AgentNode({ data }) {
  const isText = data.agentType === 'text';
  const { isEntry, external, clickable, testable } = data;

  const handleStyle = { opacity: 0, width: 6, height: 6, border: 'none' };

  return (
    <Sheet
      variant="outlined"
      color={isEntry ? 'primary' : 'neutral'}
      title={clickable ? (testable ? 'Click to edit or test this agent' : 'Click to edit this agent') : undefined}
      sx={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        boxSizing: 'border-box',
        p: 1.25,
        borderRadius: 'md',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        opacity: external ? 0.6 : 1,
        borderStyle: external ? 'dashed' : 'solid',
        borderWidth: isEntry ? 2 : 1,
        boxShadow: isEntry ? 'sm' : 'none',
        bgcolor: 'background.surface',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
        ...(clickable ? { '&:hover': { boxShadow: 'md', transform: 'translateY(-1px)' } } : {}),
      }}
    >
      <Handle id="t" type="target" position={Position.Top} style={handleStyle} />
      <Handle id="l" type="target" position={Position.Left} style={handleStyle} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {isText
          ? <SmartToyIcon fontSize="small" color="action" />
          : <RecordVoiceOverIcon fontSize="small" color="action" />}
        <Typography level="title-sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {data.name}
        </Typography>
        {isEntry && (
          <Chip size="sm" variant="solid" color="primary" startDecorator={<StarIcon sx={{ fontSize: 14 }} />}>
            main
          </Chip>
        )}
      </Box>

      {data.label && (
        <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary' }} noWrap>
          {data.label}
        </Typography>
      )}

      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Chip size="sm" variant="soft" color={isText ? 'warning' : 'success'}>
          {isText ? 'text' : 'voice'}
        </Chip>
        {data.modelName && (
          <Typography level="body-xs" noWrap sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}>
            {data.modelName}
          </Typography>
        )}
      </Box>

      <Handle id="r" type="source" position={Position.Right} style={handleStyle} />
      <Handle id="b" type="source" position={Position.Bottom} style={handleStyle} />
    </Sheet>
  );
}
